import React, { useEffect, useMemo, useRef, useState } from "react"
import icon from "../../assets/logo_black.svg"
import { Dialog, DialogPanel } from "@headlessui/react"
import {
  Bars3Icon,
  XMarkIcon,
  ChevronDoubleDownIcon,
  ChevronDoubleRightIcon,
} from "@heroicons/react/24/outline"
import { Link, useNavigate } from "react-router-dom"
import { H5 } from "../../components/typography/Typography"
import { ProductsPanel } from "./ProductsPanel"
import { JobRefPanel } from "./JobRefPanel"
import { IndustriesPanel } from "./IndustriesPanel"
import { CompanyPanel } from "./CompanyPanel"
import { ANALYTICS_EVENTS, track } from "../../lib/analytics"
import { WhatsAppIcon } from "../../components/ui-blocks/WhatsAppIcon"


type NavItem = {
  name: string
  href?: string
  isNested?: boolean
  panel?: React.ReactNode
}

type NavigationProps = {
  onClick: () => void
}

const navigation = ({ onClick }: NavigationProps): NavItem[] => {
  return [
    { name: "Products", isNested: true, panel: <ProductsPanel onClick={onClick} /> },
    { name: "Industries", href: "/industries", isNested: true, panel: <IndustriesPanel onClick={onClick} /> },
    { name: 'Job References', isNested: true, panel: <JobRefPanel onClick={onClick} /> },
    { name: "Company", isNested: true, panel: <CompanyPanel onClick={onClick} /> },
    { name: "Blogs", href: "/blog" },
    { name: "Contact", href: "/contact" },
    { name: "Login", href: "/login" },
  ]
}

export const Navbar = () => {
  const navigate = useNavigate()

  // Mobile
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [mobileView, setMobileView] = useState<"root" | "nested">("root")
  const [mobileNestedItem, setMobileNestedItem] = useState<NavItem | null>(null)

  // Desktop nested panel
  const [activeNested, setActiveNested] = useState<NavItem | null>(null)
  const closeTimer = useRef<number | null>(null)

  const onNavigate = (href?: string) => {
    if (!href) return
    setMobileMenuOpen(false)
    setMobileView("root")
    setMobileNestedItem(null)
    navigate(href)
  }

  // Close desktop panel on route change / escape
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setActiveNested(null)
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [])

  const openDesktopPanel = (item: NavItem) => {
    if (closeTimer.current) window.clearTimeout(closeTimer.current)
    if (activeNested?.name !== item.name) {
      track(ANALYTICS_EVENTS.NAV_DROPDOWN_OPENED, { panel: item.name, surface: 'desktop' })
    }
    setActiveNested(item)
  }

  const scheduleCloseDesktopPanel = () => {
    if (closeTimer.current) window.clearTimeout(closeTimer.current)
    closeTimer.current = window.setTimeout(() => setActiveNested(null), 120)
  }

  const activePanel = useMemo(() => activeNested?.panel ?? null, [activeNested])

  return (
    <div className="sticky top-0 z-50 bg-white lg:static md:mx-8 md:mt-4 md:bg-white md:rounded-xl">
      <header className="inset-x-0 top-0 z-50 relative md:rounded-xl md:border md:border-white md:hover:border-gray-200">
        <nav aria-label="Global" className="flex items-center justify-between p-6 lg:px-6 xl:px-8">
          <div className="flex lg:flex-1 lg:min-w-0">
            <Link
              to="/"
              className="-m-1.5 p-1.5"
              onClick={() => track(ANALYTICS_EVENTS.NAV_LOGO_CLICKED, { surface: 'header' })}
            >
              <img alt="logo" src={icon} className="h-10 w-auto" />
            </Link>
          </div>

          {/* Mobile actions */}
          <div className="flex lg:hidden items-center gap-4">
            <a
              href="https://wa.me/919266683150"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Contact us on WhatsApp"
              className="inline-flex items-center justify-center"
              onClick={() => track(ANALYTICS_EVENTS.WHATSAPP_CLICKED, { surface: 'navbar_mobile' })}
            >
              <WhatsAppIcon className="size-8 text-[#25D366]" />
            </a>
            <button
              type="button"
              onClick={() => {
                track(ANALYTICS_EVENTS.NAV_MOBILE_MENU_OPENED)
                setMobileMenuOpen(true)
                setMobileView("root")
                setMobileNestedItem(null)
              }}
              className="inline-flex items-center justify-center bg-dark-1 p-2 rounded-md"
            >
              <span className="sr-only">Open main menu</span>
              <Bars3Icon aria-hidden="true" className="size-6" color="white" />
            </button>
          </div>

          {/* Desktop links */}
          <div className="hidden lg:flex lg:shrink-0 lg:gap-x-4 xl:gap-x-8 2xl:gap-x-12">
            {navigation({ onClick: () => { setActiveNested(null) } }).map((item) => {
              if (item.isNested) {
                const triggerClasses = "flex flex-row items-center gap-2 whitespace-nowrap hover:opacity-40 cursor-pointer"
                return (
                  <div
                    key={item.name}
                    className="relative"
                    onMouseEnter={() => openDesktopPanel(item)}
                    onMouseLeave={scheduleCloseDesktopPanel}
                  >
                    {item.href ? (
                      <Link
                        to={item.href}
                        className={triggerClasses}
                        aria-haspopup="true"
                        aria-expanded={activeNested?.name === item.name}
                        onClick={() => track(ANALYTICS_EVENTS.NAV_LINK_CLICKED, { label: item.name, href: item.href, surface: 'desktop' })}
                      >
                        <H5 className="uppercase lg:text-sm xl:text-base">{item.name}</H5>
                        <ChevronDoubleDownIcon aria-hidden="true" className="size-4" color="#212121" />
                      </Link>
                    ) : (
                      <button
                        type="button"
                        className={triggerClasses}
                        aria-haspopup="true"
                        aria-expanded={activeNested?.name === item.name}
                      >
                        <H5 className="uppercase lg:text-sm xl:text-base">{item.name}</H5>
                        <ChevronDoubleDownIcon aria-hidden="true" className="size-4" color="#212121" />
                      </button>
                    )}
                  </div>
                )
              }

              return (
                <Link
                  key={item.name}
                  to={item.href || "#"}
                  className="hover:opacity-40"
                  onClick={() => track(ANALYTICS_EVENTS.NAV_LINK_CLICKED, { label: item.name, href: item.href, surface: 'desktop' })}
                >
                  <H5 className="uppercase cursor-pointer whitespace-nowrap lg:text-sm xl:text-base">{item.name}</H5>
                </Link>
              )
            })}
          </div>

          <div className="hidden lg:flex lg:flex-1 lg:min-w-0 lg:justify-end lg:pl-6 xl:pl-12">
            <a
              href="https://wa.me/919266683150"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Contact us on WhatsApp"
              className="inline-flex shrink-0 items-center gap-2 whitespace-nowrap rounded-full bg-[#25D366] px-3 xl:px-4 py-2 text-white font-medium text-sm shadow-sm hover:bg-[#1ebe57] transition-colors"
              onClick={() => track(ANALYTICS_EVENTS.WHATSAPP_CLICKED, { surface: 'navbar_desktop' })}
            >
              <WhatsAppIcon className="size-5" />
              <span><span className="hidden xl:inline">Chat on </span>WhatsApp</span>
            </a>
          </div>
        </nav>

        {/* Desktop 2-tier panel */}
        {activeNested && (
          <div
            className="hidden lg:block absolute left-0 right-0 top-full"
            onMouseEnter={() => {
              if (closeTimer.current) window.clearTimeout(closeTimer.current)
            }}
            onMouseLeave={scheduleCloseDesktopPanel}
          >
            {/* Backdrop (optional) */}
            <div className="absolute inset-0 -z-10" />
            <div className="w-full md:rounded-xl border-t border-gray-200 bg-white shadow-lg overflow-hidden">
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                <H5 className="uppercase">{activeNested.name}</H5>
                <button
                  type="button"
                  className="text-sm text-gray-500 hover:text-gray-700"
                  onClick={() => setActiveNested(null)}
                >
                  Close
                </button>
              </div>

              {/* Your injected component */}
              {activePanel}
            </div>
          </div>
        )}

        {/* Mobile dialog */}
        <Dialog
          open={mobileMenuOpen}
          onClose={(open) => {
            setMobileMenuOpen(open)
            if (!open) {
              setMobileView("root")
              setMobileNestedItem(null)
            }
          }}
          className="lg:hidden"
        >
          <div className="fixed inset-0 z-50" />
          <DialogPanel className="fixed inset-y-0 right-0 z-50 w-full overflow-y-auto bg-white p-6 sm:max-w-sm md:max-w-md sm:ring-1 sm:ring-gray-900/10">
            <div className="flex items-center justify-between">
              <Link
                to="/"
                className="-m-1.5 p-1.5"
                onClick={() => {
                  setMobileMenuOpen(false)
                  setMobileView("root")
                  setMobileNestedItem(null)
                }}
              >
                <span className="sr-only">Home</span>
                <img alt="logo" src={icon} className="h-8 w-auto" />
              </Link>

              <button
                type="button"
                onClick={() => {
                  setMobileMenuOpen(false)
                  setMobileView("root")
                  setMobileNestedItem(null)
                }}
                className="-m-2.5 rounded-md p-2.5 text-gray-700"
              >
                <span className="sr-only">Close menu</span>
                <XMarkIcon aria-hidden="true" className="size-6" />
              </button>
            </div>

            <div className="mt-6">
              {mobileView === "root" && (
                <div className="space-y-3">
                  {navigation({ onClick: () => { setMobileMenuOpen(false) } }).map((item) =>
                    item.isNested ? (
                      <button
                        key={item.name}
                        type="button"
                        onClick={() => {
                          track(ANALYTICS_EVENTS.NAV_DROPDOWN_OPENED, { panel: item.name, surface: 'mobile' })
                          setMobileNestedItem(item)
                          setMobileView("nested")
                        }}
                        className="w-full flex items-center justify-between rounded-lg px-2 py-3 hover:bg-gray-50"
                      >
                        <H5>{item.name}</H5>
                        <ChevronDoubleRightIcon aria-hidden="true" className="size-5" color="#212121" />
                      </button>
                    ) : (
                      <button
                        key={item.name}
                        type="button"
                        onClick={() => {
                          track(ANALYTICS_EVENTS.NAV_LINK_CLICKED, { label: item.name, href: item.href, surface: 'mobile' })
                          onNavigate(item.href)
                        }}
                        className="w-full text-left rounded-lg px-2 py-3 hover:bg-gray-50"
                      >
                        <H5>{item.name}</H5>
                      </button>
                    )
                  )}
                </div>
              )}

              {mobileView === "nested" && mobileNestedItem && (
                <div>
                  <div className="flex items-center justify-between pb-3 border-b border-gray-200">
                    <button
                      type="button"
                      onClick={() => {
                        setMobileView("root")
                        setMobileNestedItem(null)
                      }}
                      className="text-sm text-gray-600 hover:text-gray-900"
                    >
                      Back
                    </button>
                    <H5 className="uppercase">{mobileNestedItem.name}</H5>
                    <div className="w-10" />
                  </div>

                  <div className="pt-4">
                    {/* Same injected component */}
                    {mobileNestedItem.panel}
                  </div>
                </div>
              )}
            </div>
          </DialogPanel>
        </Dialog>
      </header>
    </div>
  )
}
